import type { AssignmentPiece } from '@/types/Models/Assignment'
import type { AssignmentCreationItemData } from '@/types/Models/Assignment'
import type { Item, ItemData } from '@/types/Models/Item'
import type { Product } from '@/types/Models/Product'
import { toRaw } from 'vue'

export default class ItemTransformer {
  /**
   * Method used to transform a fetched Assignment's Item.
   */
  static fetch(item: Item | ItemData): Item {
    const itemData = {
      weight: parseInt(item.weight?.toString() || '0', 10),
      width: parseInt(item.width?.toString() || '0', 10),
      height: parseInt(item.height?.toString() || '0', 10),
      length: parseInt(item.length?.toString() || '0', 10),
      estimatedCost: parseInt(item.estimatedCost?.toString() || '0', 10),
      recipientAddress: item.recipientAddress || {},
      products: item.products || [],
      product: item.product as Product | undefined,
      validationFlags: {},
    }
    if (Array.isArray(item.validationFlags)) {
      itemData.validationFlags = item.validationFlags.reduce(
        (acc: Record<string, { level: string; reason: string }>, cv) => {
          acc[cv.field] = { level: cv.level, reason: cv.reason }
          return acc
        },
        {} as Record<string, { level: string; reason: string }>,
      )
    }
    if (itemData.products?.length) {
      itemData.product = itemData.products[0]
    }
    return { ...item, ...itemData } as Item
  }

  static create(item: AssignmentCreationItemData, includeDdsReferences = false) {
    const itemData = { ...toRaw(item) } as any
    delete itemData.pieces

    // Aggregate pieces into single item dimensions (sum weight and height, max width and length)
    itemData.estimated_cost = item.pieces.reduce((acc: number, cv: AssignmentPiece) => acc + +cv.estimated_cost, 0)
    itemData.weight = item.pieces.reduce((acc: number, cv: AssignmentPiece) => acc + +cv.weight, 0)
    itemData.height = item.pieces.reduce((acc: number, cv: AssignmentPiece) => acc + +cv.height, 0)
    itemData.length = Math.max(...item.pieces.map((p: AssignmentPiece) => +p.length))
    itemData.width = Math.max(...item.pieces.map((p: AssignmentPiece) => +p.width))

    // Create single aggregated product (like Shopify integration)
    const aggregatedDescription = item.pieces
      .filter((p: AssignmentPiece) => p.description && p.description.trim().length > 0)
      .map((p: AssignmentPiece) => `${p.quantity || 1}x ${p.description}`)
      .join(', ')

    itemData.products = [
      {
        weight: itemData.weight,
        width: itemData.width,
        height: itemData.height,
        length: itemData.length,
        estimatedCost: itemData.estimated_cost,
        description: aggregatedDescription,
        commodityCode: item.pieces[0]?.commodity_code || '',
        quantity: 1,
      },
    ]

    // CN22 with separate products for international shipments (outside SE)
    if (item.country_code && item.country_code !== 'SE') {
      itemData.cn22 = {
        hsCode: item.pieces[0]?.commodity_code || '',
        category: item.invoiceType || 'other',
        currency: item.currency || 'SEK',
        originCountryCode: item.pieces[0]?.origin_country_code || 'SE',
        products: item.pieces.map((p: AssignmentPiece) => ({
          description: p.description,
          quantity: p.quantity || 1,
          currency: item.currency || 'SEK',
          weight: p.weight,
          estimatedCost: p.estimated_cost,
          commodityCode: p.commodity_code,
          countryCode: p.origin_country_code || 'SE',
          ...(includeDdsReferences && p.ddsReference ? { eudr: { ddsReference: p.ddsReference } } : {}),
        })),
      }
    }

    if (item.pieces.length > 0 && item.pieces[0]?.description) {
      itemData.reference = item.pieces[0].description
    }

    return itemData
  }

  /**
   * Method used to transform a fetched collection.
   */
  static fetchCollection(items: Item[] | ItemData[]) {
    return items.map((item) => this.fetch(item))
  }
}
